import { useMemo } from 'react';

import { MinimalRecentOrderData } from 'features/menu/pages/recent-items-page/minimal-recent-items/types';
import { useMinimalRecentEntryAvailability } from 'features/menu/pages/recent-items-page/minimal-recent-items/use-minimal-recent-entry-availability';
import { useMinimalRecentItems } from 'features/menu/pages/recent-items-page/minimal-recent-items/use-minimal-recent-items';
import { useStoreContext } from 'features/store';

import { ItemAvailabilityStatus } from 'enums/menu';
import { RbiOrderStatus } from 'generated/graphql-gateway';
import { useIsOrderInProgress } from 'hooks/use-is-order-in-progress';

import { MinimalMappedCartEntry, MinimalMappedOrder } from './minimal-menu-reorder-widget-types';

const hasSufficientAvailability = (orderEntries: MinimalMappedCartEntry[]): boolean => {
  const unavailableCartEntries = orderEntries.filter(
    entry =>
      entry.status !== ItemAvailabilityStatus.AVAILABLE &&
      entry.status !== ItemAvailabilityStatus.CART_EXTRA
  ).length;
  const totalItemsInCart = orderEntries.length || 0;
  return totalItemsInCart / 2 >= unavailableCartEntries;
};

const mapSelections = (entry: MinimalRecentOrderData['minimalEntries'][number]) => {
  if (!entry.children?.length) {
    return [];
  }

  return entry.children.map((child, index) => ({
    id: `${entry.id}-${child.id}-${index}`,
    element: `${child.quantity}x ${child.name}`,
  }));
};

export const useLastMinimalOrderAvailableToReorder = (): MinimalMappedOrder | undefined => {
  const isOrderInProgress = useIsOrderInProgress();
  const { noStoreSelected, isStoreOpenAndAvailable } = useStoreContext();
  const { orders } = useMinimalRecentItems(
    true,
    {
      limit: 1,
      orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.PAYMENT_SUCCESSFUL],
    },
    'no-cache'
  );

  const lastOrder = orders?.[0];
  const { availability } = useMinimalRecentEntryAvailability(lastOrder?.minimalEntries ?? []);

  const mappedOrder = useMemo<MinimalMappedOrder | undefined>(() => {
    if (!lastOrder) {
      return undefined;
    }

    const orderEntries: MinimalMappedCartEntry[] = lastOrder.minimalEntries.map((entry, index) => {
      const itemAvailabilityStatus =
        availability[index]?.itemAvailabilityStatus ?? ItemAvailabilityStatus.UNAVAILABLE;

      return {
        ...entry,
        itemSelections: mapSelections(entry),
        dayParts: availability[index]?.dayParts ?? [],
        status: itemAvailabilityStatus,
        isAvailable: [ItemAvailabilityStatus.AVAILABLE, ItemAvailabilityStatus.CART_EXTRA].includes(
          itemAvailabilityStatus
        ),
      };
    });

    if (!hasSufficientAvailability(orderEntries)) {
      return undefined;
    }

    return {
      ...lastOrder,
      orderEntries,
    };
  }, [availability, lastOrder]);

  if (noStoreSelected || !isStoreOpenAndAvailable || isOrderInProgress) {
    return undefined;
  }

  return mappedOrder;
};
